import { z } from 'zod';
import { MAX_PHOTOS_PER_VISIT } from './photo.js';
import { GeoPoint, Id, IdempotencyKey, Timestamp } from './primitives.js';
/**
 * FR-07 / FR-08 visit lifecycle.
 *
 * The defining oddity of this system: SUBMIT DOES NOT CLOSE THE SESSION.
 * After submit the user is still considered to be in the store and may add more
 * photos or submit again; only an explicit check-out closes the visit.
 * TSP 2.2.1 flags this as an unusual design and a reliable bug source, so the
 * machine is defined once here and imported by both the Zustand client store
 * and the NestJS visits module — there is no second copy to drift.
 */
export const VisitState = z.enum([
    /** Checked in, nothing submitted yet. */
    'OPEN',
    /** At least one submit has happened. Session REMAINS ACTIVE (FR-07). */
    'SUBMITTED',
    /** Explicit check-out has happened. Terminal (FR-08). */
    'CLOSED',
]);
export const VisitEvent = z.enum(['CHECK_IN', 'ADD_PHOTO', 'SUBMIT', 'CHECK_OUT']);
/**
 * The complete legal transition table. Anything not listed here is illegal and
 * must fail cleanly while still leaving a correct audit trail (TSP 2.2.1).
 *
 * Note ADD_PHOTO and SUBMIT are both self-loops on SUBMITTED — that is the
 * FR-07 behaviour, not an oversight.
 */
export const VISIT_TRANSITIONS = Object.freeze({
    OPEN: { ADD_PHOTO: 'OPEN', SUBMIT: 'SUBMITTED', CHECK_OUT: 'CLOSED' },
    SUBMITTED: { ADD_PHOTO: 'SUBMITTED', SUBMIT: 'SUBMITTED', CHECK_OUT: 'CLOSED' },
    CLOSED: {},
});
/** Single source of truth for "may this event fire now?". */
export function nextVisitState(from, event) {
    return VISIT_TRANSITIONS[from][event] ?? null;
}
/**
 * The illegal transitions TSP 2.2.1 requires to be tested explicitly.
 * Exported so the test suite cannot silently drop one.
 */
export const ILLEGAL_TRANSITIONS = Object.freeze([
    { from: 'CLOSED', event: 'SUBMIT', case: 'submit after check-out' },
    { from: 'CLOSED', event: 'ADD_PHOTO', case: 'add photo after check-out' },
    { from: 'CLOSED', event: 'CHECK_OUT', case: 'double check-out' },
    { from: 'CLOSED', event: 'CHECK_IN', case: 'check-in on a closed visit' },
    { from: 'OPEN', event: 'CHECK_IN', case: 'double check-in' },
    { from: 'SUBMITTED', event: 'CHECK_IN', case: 'check-in on an active visit' },
]);
/**
 * FR-03 / FR-08: GPS validation outcome. Out-of-tolerance NEVER blocks.
 *
 * `UNAVAILABLE` and `NOT_CONFIGURED` are both "no verdict", and keeping them
 * apart is the whole point of having two. The first is the DEVICE — permission
 * denied, or an indoor fix that never resolved. The second is the MASTER — an
 * outlet registered before anyone stood in front of it with a phone, so there is
 * no coordinate to measure against. Collapsing them would make a gap in the
 * master data read as a phone problem in every report, and nobody would ever go
 * and fix the outlet.
 */
export const GpsValidation = z.enum([
    'IN_TOLERANCE',
    'OUT_OF_TOLERANCE',
    /** The device produced no fix. */
    'UNAVAILABLE',
    /** The outlet has no master coordinate. The radius check was skipped, not failed. */
    'NOT_CONFIGURED',
]);
export const GpsReading = z.object({
    point: GeoPoint.nullable(),
    /** Straight-line metres from the outlet master coordinate. */
    distanceM: z.number().nonnegative().nullable(),
    toleranceM: z.number().int().positive(),
    validation: GpsValidation,
    readAt: Timestamp,
});
export const CheckInRequest = z.object({
    outletId: Id,
    gps: GeoPoint.nullable(),
    idempotencyKey: IdempotencyKey,
});
/**
 * FR-03: when out of tolerance the system shows a warning and the user proceeds
 * WITHOUT having to enter a reason or give extra confirmation. `warning` is
 * therefore informational — there is no `blocked` field by design.
 */
export const CheckInResponse = z.object({
    visitId: Id,
    state: z.literal('OPEN'),
    checkInAt: Timestamp,
    gps: GpsReading,
    warning: z.string().nullable(),
});
export const SubmitRequest = z.object({
    visitId: Id,
    idempotencyKey: IdempotencyKey,
});
export const SubmitResponse = z.object({
    visitId: Id,
    /** Always SUBMITTED — never CLOSED. The session stays active (FR-07). */
    state: z.literal('SUBMITTED'),
    submittedAt: Timestamp,
    photoCount: z.number().int().min(0).max(MAX_PHOTOS_PER_VISIT),
    message: z.string(),
});
export const CheckOutRequest = z.object({
    visitId: Id,
    gps: GeoPoint.nullable(),
    idempotencyKey: IdempotencyKey,
});
export const CheckOutResponse = z.object({
    visitId: Id,
    state: z.literal('CLOSED'),
    checkOutAt: Timestamp,
    gps: GpsReading,
    /** FR-08: check-out − check-in, logged to the audit trail. */
    durationSeconds: z.number().int().nonnegative(),
    warning: z.string().nullable(),
});
//# sourceMappingURL=visit.js.map